import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { navigate } from "gatsby";

import { useAddToCartWithLuxe } from "@context/LuxeProvider";
import { CartContext } from "@context/CartProvider";
import { useTracking } from "@context/Tracking";

import useDiscountsInfo from "@hooks/useDiscountsInfo";
import { useCurrentCountryCode, useCurrentCurrencyCode } from "@hooks/usePrices";
import useBundleProductsWithPrices from "@hooks/useBundleProductsWithPrices";

import { roundNumber } from "@helpers/general";
import { useApplyIgPricesOnProducts, useApplyIgPricesOnVariants } from "@helpers/intelligems";
import { useALError } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { getPreOrderShipDate, isPreorder, prepareAddToCart } from "@helpers/product";
import { getMtoType } from "@helpers/mto";

import { applyCartDiscounts } from "@components/cartDiscounts";

import { ComponentType } from "@constants/ComponentType";
import TAG from "@constants/Tag";

const useCart = (
  product,
  collectionHandle = "",
  isLuxe = false,
  componentType = "",
  mtoSettings = null
) => {
  const { cart, isLoadingCart, toggleCart } = useContext(CartContext);
  const isByob =
    componentType === ComponentType.CP_BYOB || componentType === ComponentType.CP_BYOB_QUICKVIEW;

  const [isATCComplete, setIsATCComplete] = useState(false);

  const countryCode = useCurrentCountryCode();
  const currencyCode = useCurrentCurrencyCode();

  const fullProduct = useMemo(
    () => ({ ...product?.node, ...product?.contentful?.node, ...product }),
    [product]
  );

  const { products: bundleProductsWithPrices } = useBundleProductsWithPrices(
    fullProduct.bundleProducts,
    countryCode
  );

  const productCopy = useMemo(
    () => ({ ...fullProduct, bundleProducts: bundleProductsWithPrices }),
    [fullProduct, bundleProductsWithPrices]
  );

  const igPricesForProduct = useApplyIgPricesOnVariants(
    productCopy.shopifyId,
    productCopy.variants
  );
  const igPricesForBundle = useApplyIgPricesOnProducts(
    productCopy.bundleProducts?.map((p) => ({
      productId: p.node.id,
      variant: p.node.variants[0],
      prices: p,
    })) ?? []
  );

  const { sendReport } = useALError();
  const discountsInfo = useDiscountsInfo();
  const addToCartWithLuxe = useAddToCartWithLuxe();
  const { trackByobCartActions } = useTracking();
  const sideCartInfo = applyCartDiscounts(cart.lines, discountsInfo);

  const trackBYOBAddToCart = useCallback(() => {
    const byobItems = sideCartInfo.lines.filter(
      (line) =>
        line.attributes.some((attr) => attr.key === "_isBYOB" && attr.value === "true") &&
        line.attributes.some(
          (attr) => attr.key === "_collectionHandle" && attr.value === collectionHandle
        )
    );
    const totalQuantity = byobItems.reduce((count, item) => count + item.quantity, 0);
    if (totalQuantity >= 2) {
      toggleCart();
    }
    trackByobCartActions({
      action: "add",
      productHandle: product?.node?.handle,
      collectionHandle,
      noOfProducts: totalQuantity,
      allSavings: roundNumber(
        sideCartInfo.fullAmount - (isLuxe ? sideCartInfo.finalAmountLux : sideCartInfo.finalAmount)
      ),
    });
  }, [
    sideCartInfo.lines,
    sideCartInfo.fullAmount,
    sideCartInfo.finalAmountLux,
    sideCartInfo.finalAmount,
    trackByobCartActions,
    product?.node?.handle,
    collectionHandle,
    isLuxe,
    toggleCart,
  ]);

  useEffect(() => {
    if (isATCComplete && !isLoadingCart && isByob) {
      setIsATCComplete(false);
      trackBYOBAddToCart();
    }
  }, [sideCartInfo, componentType, isATCComplete, isLoadingCart, trackBYOBAddToCart, isByob]);

  const quickAddToCart = useCallback(
    async ({ shouldOpenCart = true, selectedVariant = null } = {}) => {
      try {
        const variant = selectedVariant || fullProduct.variants?.[0];
        const isPreOrder = isPreorder(fullProduct, variant);
        const igPrices = igPricesForProduct[variant.id];
        const productPrices = {
          ...igPrices,
          compareAtPrice: igPrices.finalPrice !== igPrices.fullPrice ? igPrices.fullPrice : null,
        };
        const mtoTimeline =
          fullProduct?.madeToOrderTimelines && fullProduct?.madeToOrderTimelines !== "null"
            ? fullProduct.madeToOrderTimelines
            : null;

        const { dataProductsToAdd } = prepareAddToCart({
          product: productCopy,
          selectedVariant: variant,
          productPrices,
          bundlePrices: igPricesForBundle,
          productOverrides: {
            isPreOrder,
            preOrderShipDate: getPreOrderShipDate(productCopy, variant) || null,
            mtoTimeline,
            mtoType: getMtoType(fullProduct),
            isFinalSale:
              productCopy?.tags?.includes(TAG.FINAL_SALE) ||
              productCopy?.tags?.includes(TAG.CUSTOMIZABLE),
            mtoSettings,
          },
          countryCode,
          currencyCode,
          discountsInfo,
          isByob,
          collectionHandle,
        });

        if (dataProductsToAdd.length > 0) {
          await addToCartWithLuxe({
            dataProductsToAdd,
            shouldOpenSideCart: shouldOpenCart,
            componentType,
          });
        }
        setIsATCComplete(true);
      } catch (error) {
        sendReport(error, { name: "useCart", priority: "P1" });
        if (fullProduct?.handle) {
          navigate(`/products/${fullProduct.handle}`);
        }
      }
    },
    [
      addToCartWithLuxe,
      collectionHandle,
      componentType,
      discountsInfo,
      fullProduct,
      countryCode,
      currencyCode,
      isByob,
      mtoSettings,
      sendReport,
      productCopy,
      igPricesForProduct,
      igPricesForBundle,
    ]
  );

  return useMemo(() => ({ quickAddToCart }), [quickAddToCart]);
};

export default useCart;
